import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost } from '../services/api';
import './CalculadoraPage.css';

// ── Constantes ──────────────────────────────────────────────────────────────
const DISCOUNT_COMMISSION = { 0: 5, 5: 4.5, 10: 4, 15: 3, 20: 2 };
const TAPA_PRICE = 4.20;
const IVA_RATE   = 0.21;

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmt(n) {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function sepLabel(s) {
  if (s === 2)    return '2,00 m';
  if (s === 1.45) return '1,45 m';
  return s + ' m';
}

/** Calcula filas de postes según material, fijación y cantidades */
function calcPostes(td, nFin, nInt, nEsq, posteMat, posteFix) {
  if (!td) return [];
  const rows = [];
  const add  = (label, pu, qty) => {
    if (!pu || qty <= 0) return;
    rows.push({ name: label, qty, pu, sub: pu * qty });
  };
  if (posteMat === 'pvc') {
    const pp = (posteFix === 'zapatilla' && td.pzap)  ? td.pzap
             : (posteFix === 'tubo'      && td.ptubo) ? td.ptubo
             : td.psin || td.ptubo;
    if (!pp) return rows;
    const fixTxt = (posteFix === 'zapatilla' && td.pzap)  ? 'con zapatilla'
                 : (posteFix === 'tubo'      && td.ptubo) ? 'con tubo'
                 : 'sin zap.';
    const base = `Poste PVC ${td.h}cm (${fixTxt})`;
    add(`${base} · Final`,      pp.f, nFin);
    add(`${base} · Intermedio`, pp.i, nInt);
    add(`${base} · Esquinero`,  pp.e, nEsq);
  } else {
    const al = td.alu;
    if (!al) return rows;
    const base = `Poste Aluminio ${td.h}cm`;
    add(`${base} · Final Pared`, al.fp, nFin);
    add(`${base} · Intermedio`,  al.i,  nInt);
    add(`${base} · Esquinero`,   al.e,  nEsq);
  }
  return rows;
}

/** Busca el tipo de poste de referencia para una altura personalizada (redondeo a la alza) */
function findPosteEspecialRef(altCm, tipos, posteMat, posteFix) {
  const entries = Object.entries(tipos).filter(([, v]) => v.h != null);
  if (!entries.length) return null;
  entries.sort((a, b) => a[1].h - b[1].h);
  const found = entries.find(([, v]) => v.h >= altCm) || entries[entries.length - 1];
  if (!found) return null;
  const [tipoKey, tipoData] = found;
  let precio = null;
  if (posteMat === 'pvc') {
    const pp = (posteFix === 'zapatilla' && tipoData.pzap)  ? tipoData.pzap
             : (posteFix === 'tubo'      && tipoData.ptubo) ? tipoData.ptubo
             : tipoData.psin || tipoData.ptubo;
    if (pp) precio = pp.f;
  } else {
    if (tipoData.alu) precio = tipoData.alu.fp;
  }
  return { tipoKey, tipoData, precio };
}

/** Obtiene el precio del poste esquinero según material y fijación */
function getEsquineroPrice(td, posteMat, posteFix) {
  if (!td) return null;
  if (posteMat === 'pvc') {
    const pp = (posteFix === 'zapatilla' && td.pzap)  ? td.pzap
             : (posteFix === 'tubo'      && td.ptubo) ? td.ptubo
             : td.psin || td.ptubo;
    return pp?.e || null;
  }
  return td.alu?.e || null;
}

/** Abre un HTML string como blob en pestaña nueva (igual que CRM original) */
function openBlob(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const tab  = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  if (!tab) {
    downloadBlob(html, filename);
  }
}

/** Descarga directa como archivo HTML (sin popup, no se bloquea) */
function downloadBlob(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '.html';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Envuelve el contenido en el HTML completo del PDF (mismos estilos que CRM original) */
function buildPDFShell(titulo, sheetHTML, brand = '#E84300') {
  return `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"><title>${titulo}</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
@page{size:A4 portrait;margin:14mm 15mm}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',Arial,sans-serif;background:#fff;color:#1A1A1A;-webkit-print-color-adjust:exact;print-color-adjust:exact;padding:14mm 15mm}
.sheet{max-width:750px;margin:0 auto}
@media print{body{padding:0}}
@media print{.no-print{display:none!important}}
.pf-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:22px}
.pf-doc-info{text-align:right}
.pf-doc-num{font-family:'DM Mono',monospace;font-size:8px;color:#888;text-transform:uppercase;letter-spacing:.12em}
.pf-doc-date{font-size:11px;color:#444;margin-top:4px}
.pf-doc-label{background:${brand};color:#fff;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;padding:3px 9px;border-radius:3px;margin-top:7px;display:inline-block}
.pf-divider{height:2px;background:linear-gradient(to right,${brand},#e0e0e0);margin-bottom:18px}
.pf-client-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:16px}
.pf-client-label{font-size:7px;font-weight:700;text-transform:uppercase;letter-spacing:.15em;color:#888;margin-bottom:4px}
.pf-client-name{font-size:13px;font-weight:600;margin-bottom:2px}
.pf-client-detail{font-size:10px;color:#555;line-height:1.5}
.pf-config-box{background:#F7F7F4;border-radius:6px;padding:10px 13px;margin-bottom:18px;display:flex;gap:18px;flex-wrap:wrap}
.ci-label{font-size:7px;color:#888;text-transform:uppercase;letter-spacing:.1em;font-weight:700}
.ci-val{font-size:11px;font-weight:600;margin-top:2px}
table{width:100%;border-collapse:collapse;margin-bottom:14px}
thead tr{background:#ddd4d0}
thead th{padding:6px 9px;text-align:left;font-size:7px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;color:rgba(0,0,0,.5)}
thead th:nth-child(3),thead th:nth-child(4),thead th:last-child{text-align:right}
tbody tr{border-bottom:1px solid #F0F0EC}
tbody tr:nth-child(even){background:#FAFAF8}
tbody td{padding:7px 9px;font-size:11px;vertical-align:middle}
tbody td:nth-child(1){font-family:'DM Mono',monospace;font-size:8px;color:#888}
tbody td:nth-child(3),tbody td:nth-child(4),tbody td:last-child{text-align:right;font-family:'DM Mono',monospace;font-size:10px}
.pf-total-row{display:flex;justify-content:space-between;padding:4px 0;font-size:11px;color:#555;border-bottom:1px solid #eee}
.pf-total-row.main-total{font-size:14px;font-weight:700;color:#1A1A1A;border-top:2px solid #1A1A1A;border-bottom:none;padding-top:8px;margin-top:4px}
.pf-total-row.main-total span:last-child{color:#B83000}
.pf-contado-wrap{display:flex;align-items:center;gap:10px;margin-bottom:14px;background:#fafaf8;border:1px solid #eee;border-radius:10px;padding:12px 16px}
.pf-contado-left,.pf-contado-right{flex:1;text-align:center}
.pf-contado-badge{display:inline-block;font-size:6px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;background:#f0f0f0;color:#999;border-radius:3px;padding:2px 7px;margin-bottom:4px}
.pf-contado-badge.ok{background:${brand};color:#fff}
.pf-contado-was{font-size:20px;font-weight:700;color:#ccc;text-decoration:line-through;line-height:1.1}
.pf-contado-arrow{font-size:22px;color:${brand};font-weight:900;flex-shrink:0}
.pf-contado-price{font-size:28px;font-weight:800;color:${brand};letter-spacing:-.02em;line-height:1.1}
.pf-contado-saving{display:inline-block;background:#E8F5E9;color:#2E7D32;font-size:7px;font-weight:700;border-radius:3px;padding:2px 8px;margin-top:5px}
.pf-hero-financ{background:#003087;border-radius:12px;padding:20px 24px;margin-bottom:18px;color:#fff}
.pf-hero-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
.pf-hero-bbva{font-size:22px;font-weight:900;letter-spacing:.08em;color:#FFD700;display:block;line-height:1}
.pf-hero-tagline{font-size:7px;color:rgba(255,255,255,.7);letter-spacing:.1em;text-transform:uppercase;margin-top:3px}
.pf-hero-zero{font-size:44px;font-weight:900;color:#FFD700;line-height:1;letter-spacing:-.03em}
.pf-hero-cuota-label{font-size:7px;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:rgba(255,255,255,.6);margin-bottom:3px}
.pf-hero-cuota{font-size:52px;font-weight:900;line-height:1;letter-spacing:-.03em;color:#fff}
.pf-hero-cuota span{font-size:18px;font-weight:400;color:rgba(255,255,255,.7)}
.pf-hero-pills{display:flex;flex-wrap:wrap;gap:5px;margin:10px 0}
.pill{background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.25);color:#fff;font-size:7px;font-weight:600;padding:3px 10px;border-radius:50px}
.pf-hero-total{font-size:8px;color:rgba(255,255,255,.5);border-top:1px solid rgba(255,255,255,.15);padding-top:7px;margin-top:4px;line-height:1.6}
.pf-notes{border-radius:0 6px 6px 0;padding:10px 13px;margin-bottom:12px}
.pf-notes-title{font-size:7px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px}
.pf-notes p{font-size:10px;color:#555;line-height:1.55}
.pf-footer{border-top:1px solid #eee;padding-top:10px;display:flex;justify-content:space-between;align-items:center;margin-top:16px}
.pf-footer span{font-size:8px;color:#888}
.print-bar{background:#1A1A1A;padding:12px 18px;display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;border-radius:8px}
.print-bar span{color:rgba(255,255,255,.7);font-size:12px}
.print-btn{background:${brand};color:#fff;border:none;padding:8px 18px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit}
</style></head><body>
<div class="sheet">
  <div class="print-bar no-print">
    <span>📄 ${titulo.replace(/_/g, ' ')}</span>
    <button class="print-btn" onclick="window.print()">⬇ Guardar como PDF</button>
  </div>
  ${sheetHTML}
</div>
<script>setTimeout(function(){ window.print(); }, 1200);<\/script>
</body></html>`;
}

// ── Fila editable de material ────────────────────────────────────────────────
function EditRow({ it, onUpdate, onRemove, fmt }) {
  return (
    <div className="bp-edit-row">
      <input
        className="bp-edit-name"
        value={it.name}
        onChange={e => onUpdate(it._key, 'name', e.target.value)}
        placeholder="Descripción"
      />
      <div className="bp-edit-nums">
        <label>Cant.</label>
        <input type="number" min="0" step="any" value={it.qty}
          onChange={e => onUpdate(it._key, 'qty', e.target.value)} />
        <label>P/U €</label>
        <input type="number" min="0" step="any" value={it.pu}
          onChange={e => onUpdate(it._key, 'pu', e.target.value)} />
        <span className="bp-edit-sub">{fmt(parseFloat(it.sub) || 0)}</span>
        <button className="bp-edit-del" onClick={() => onRemove(it._key)}>✕</button>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function CalculadoraPage({ initialCliente }) {
  const { auth } = useContext(AuthContext);
  const tenantName    = auth.tenant?.name || 'Mi empresa';
  const tenantLogo    = auth.tenant?.logo || null;
  const tenantCompany = auth.tenant?.company_data || {};
  const brand         = auth.tenant?.colors?.brand || '#E84300';
  const userRole      = auth.user?.role || 'comercial';
  const isComercial   = userRole === 'comercial';
  const isSuperadmin  = userRole === 'superadmin';
  const hasFinanciacion = isSuperadmin || (auth.modules || []).includes('financiacion');

  // ── Config de financiación/descuentos (la edita el admin en Ajustes → Financiación) ──
  // Se guarda en company_data.financiacion. Los defaults replican el comportamiento previo
  // (recargo 10%, 36 meses) para no cambiar nada hasta que el admin lo configure.
  const getFinCfg = (fc) => {
    const f = (fc && fc.financiacion) || tenantCompany.financiacion || {};
    const recargo  = Number(f.recargo_pct);
    const meses    = Number(f.meses);
    const ficticio = Number(f.dto_ficticio_pct);
    const recargoPct = Number.isFinite(recargo) ? recargo : 10;
    return {
      recargoPct,
      recFactor:   1 + recargoPct / 100,
      meses:       Number.isFinite(meses) && meses > 0 ? meses : 36,
      // % de descuento "ficticio" mostrado en el presupuesto. Si no se define:
      //  · con módulo financiación → = recargo (mantiene el "Ahorro 10%" de siempre)
      //  · sin módulo → 0 (contado limpio, salvo que el admin ponga un dto ficticio)
      ficticioPct: Number.isFinite(ficticio) ? ficticio : (hasFinanciacion ? recargoPct : 0),
    };
  };
  const finLive = getFinCfg();

  // Cargar % comisión/margen del usuario
  const [myRates, setMyRates] = useState({ commission_pct: null, margin_pct: null });
  useEffect(() => {
    apiGet('/users/me/rates', auth.token)
      .then(r => setMyRates(r))
      .catch(() => {});
  }, [auth.token]);

  // ── Estado ──────────────────────────────────────────────────────────────
  const [products,     setProducts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [model,        setModel]        = useState('');
  const [tipo,         setTipo]         = useState('');
  const [lamaIdx,      setLamaIdx]      = useState(0);
  const [modo,         setModo]         = useState('metros'); // 'metros' | 'tramos'
  const [metros,       setMetros]       = useState('');
  const [tramos,       setTramos]       = useState([]);
  const [tramoCount,   setTramoCount]   = useState(0);
  const [posteMat,     setPosteMat]     = useState('pvc');
  const [posteFix,     setPosteFix]     = useState('zapatilla');
  const [tapasQty,     setTapasQty]     = useState(0);
  const [instalacion,  setInstalacion]  = useState('');
  const [esquin,       setEsquin]       = useState(0);
  const [finalesExtra, setFinalesExtra] = useState(0);
  const [descuento,    setDescuento]    = useState(0);
  const [dtoManualAmt, setDtoManualAmt] = useState('');
  const [dtoManualConcepto, setDtoManualConcepto] = useState('');
  const [extraCosteAmt, setExtraCosteAmt] = useState('');
  const [extraCosteConcepto, setExtraCosteConcepto] = useState('');
  const [cliente,      setCliente]      = useState(initialCliente || { nombre: '', poblacion: '', tel: '', email: '' });
  const [toast,        setToast]        = useState(null);
  const [savingLead,   setSavingLead]   = useState(false);
  const [savedLeadId,  setSavedLeadId]  = useState(null);
  const [savingPDF,    setSavingPDF]    = useState(false);
  const [pdfModal,     setPdfModal]     = useState(null); // null | 'save' | 'download'
  // Poste especial
  const [peAltura,  setPeAltura]  = useState('');      // input cm
  const [peModal,   setPeModal]   = useState(false);   // modal elección sustitución
  const [peRef,       setPeRef]       = useState(null);  // { tipoKey, tipoData, precio }
  const [peSubst,     setPeSubst]     = useState(null);  // { postType, substType, tipoKey, h, precio, altCm }
  const [peModalStep, setPeModalStep] = useState(1);    // 1=tipo del poste, 2=qué poste elimina
  const [pePostType,  setPePostType]  = useState(null); // tipo elegido en paso 1
  const [peModalTramo, setPeModalTramo] = useState(null); // null | { tramoId, type:'especial'|'esquinero', altCm?, ref?, esquineroPrice?, step?, postType? }
  const [editItems,    setEditItems]    = useState(null); // null = auto-calculado, array = override manual (en edición activa)
  const [savedItems,   setSavedItems]   = useState(null); // items confirmados (guardados, para mostrar en modo normal)
  const [editMode,     setEditMode]     = useState(false); // true = edición activa (paneles bloqueados)
  const [selectedColor, setSelectedColor] = useState('Nogal'); // color elegido cuando modelo es _color

  // ── Carga productos ──────────────────────────────────────────────────────
  useEffect(() => {
    apiGet('/calculator/config', auth.token)
      .then(data => setProducts(data.filter(p => p.active)))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [auth.token]);

  // Mapa rápido product_key → config
  const productMap = useMemo(() => {
    const m = {};
    products.forEach(p => {
      m[p.product_key] = { ...p.config, fullName: p.product_name };
    });
    return m;
  }, [products]);

  // Modelo a color: product_key termina en _color
  const isColorModel = model.endsWith('_color');

  const currentProduct = model ? productMap[model] : null;
  const tipos  = currentProduct?.tipos  || {};
  const lamas  = currentProduct?.lamas  || [];

  // Al cambiar de modelo: solo limpia datos de sesión (la migración inteligente la hace el onChange del select)
  useEffect(() => {
    setSavedLeadId(null);
    setPeAltura(''); setPeRef(null); setPeSubst(null);
    setEditItems(null);
  }, [model]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actualizar precio de referencia del poste especial si cambia material/fijación
  useEffect(() => {
    if (!peAltura || !currentProduct) return;
    const n = parseInt(peAltura);
    if (!n || n <= 0) return;
    const ref = findPosteEspecialRef(n, tipos, posteMat, posteFix);
    setPeRef(ref || null);
    if (peSubst && ref && ref.precio != null) {
      setPeSubst(p => p ? { ...p, tipoKey: ref.tipoKey, h: ref.tipoData.h, precio: ref.precio } : null);
    } else if (peSubst) {
      setPeSubst(null);
    }
  }, [posteMat, posteFix]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al seleccionar aluminio, forzar zapatilla
  useEffect(() => {
    if (posteMat === 'aluminio') setPosteFix('zapatilla');
  }, [posteMat]);

  // ── Cálculo derivado (useMemo = recalcula automáticamente) ───────────────
  const result = useMemo(() => {
    if (!currentProduct || !model) return null;

    const inst   = parseFloat(instalacion) || 0;
    const tapas  = parseInt(tapasQty)      || 0;
    const esquins = parseInt(esquin)       || 0;
    const finExt  = parseInt(finalesExtra) || 0;

    if (modo === 'metros') {
      // ── MODO METROS ──
      if (!tipo || !tipos[tipo]) return null;
      const metrosVal = parseFloat(metros);
      if (!metrosVal || metrosVal <= 0) return null;

      const td       = tipos[tipo];
      const lamaData = lamas[lamaIdx] || lamas[0];
      if (!lamaData) return null;

      const sep        = lamaData.sep;
      const nT         = Math.ceil(metrosVal / sep);
      const nPostBase  = nT + 1;
      let   nEsquin2   = Math.min(esquins, nPostBase - 2);
      const nFinales2  = 2 + finExt;
      const nIntermed2 = Math.max(0, nPostBase - 2 - nEsquin2);
      const nPostes2   = nFinales2 + nIntermed2 + nEsquin2;
      const lamasTotal = td.n * nT;

      const items = [];
      let total   = 0;

      // Lamas
      const lamaSub = lamaData.price * lamasTotal;
      total += lamaSub;
      items.push({
        name: 'Lamas',
        ref:  lamaData.ref,
        qty:  lamasTotal,
        pu:   lamaData.price,
        sub:  lamaSub,
        detail: `${lamasTotal} uds × ${lamaData.price.toFixed(2)} € (${td.n} lamas/tramo × ${nT} tramos)`,
      });

      // Poste especial: reducir contador ANTES de calcPostes
      let specialItem = null;
      let nFinPost = nFinales2;
      let nIntPost = nIntermed2;
      if (peSubst && peSubst.precio != null) {
        const { substType, postType, tipoKey, h, precio, altCm } = peSubst;
        if      (substType === 'final'      && nFinPost > 0) nFinPost -= 1;
        else if (substType === 'intermedio' && nIntPost > 0) nIntPost -= 1;
        else if (substType === 'esquinero'  && nEsquin2 > 0) nEsquin2 -= 1;
        const tipoLabel = postType || substType;
        specialItem = { name: `Poste especial ${tipoLabel} ${altCm} cm (precio ref. ${tipoKey})`, ref: 'Poste especial', qty: 1, pu: precio, sub: precio, detail: `1 ud × ${precio.toFixed(2)} € · referencia ${tipoKey} (${h}cm)` };
      }

      // Postes (con cantidades ya ajustadas)
      calcPostes(td, nFinPost, nIntPost, nEsquin2, posteMat, posteFix).forEach(r => {
        total += r.sub;
        items.push({ ...r, ref: r.name, detail: `${r.qty} uds × ${r.pu.toFixed(2)} €` });
      });

      // Tapas
      if (tapas > 0) {
        const tapSub = TAPA_PRICE * tapas;
        total += tapSub;
        items.push({ name: 'Tapa por poste', ref: 'Tapas', qty: tapas, pu: TAPA_PRICE, sub: tapSub,
          detail: `${tapas} uds × ${TAPA_PRICE.toFixed(2)} €` });
      }
      // Instalación
      if (inst > 0) {
        total += inst;
        items.push({ name: 'Instalación y mano de obra', ref: 'Instalación', qty: 1, pu: inst, sub: inst, detail: '' });
      }

      // Añadir poste especial al desglose
      if (specialItem) { total += specialItem.sub; items.push(specialItem); }

      return {
        items, total,
        nTramos: nT, nPostes: nPostes2, nIntermed: nIntermed2, nFinales: nFinales2, nEsquin: nEsquin2,
        metros: metrosVal, lamasTotal, sep,
        lamaRef: lamaData.ref, tipo, tipoData: td,
        modelData: currentProduct, instalacion: inst,
        modoTramos: false, sections: null, tramoItems: [], specialItem,
      };

    } else {
      // ── MODO TRAMOS ──
      const validTramos = tramos.filter(t => t.metros > 0 && t.tipoKey && tipos[t.tipoKey]);
      if (validTramos.length === 0) return null;

      const sections   = [];
      const allItems   = [];
      let grandTotal   = 0;
      let totalMetros  = 0;
      let totalPostes  = 0;
      let totalTramos  = 0;

      validTramos.forEach((t, ti) => {
        const td    = tipos[t.tipoKey];
        const ldT   = lamas[t.lamaIdx] || lamas[0];
        const sepT  = ldT.sep;
        const nT    = Math.ceil(t.metros / sepT);
        let   nFin  = 2;
        let   nInt  = Math.max(0, nT - 1);
        const nLamas = td.n * nT;

        // Extras por tramo: postes especiales y esquineros
        const extraItemsList = [];
        (t.extras || []).forEach(ex => {
          if      (ex.substType === 'final'      && nFin > 0) nFin -= 1;
          else if (ex.substType === 'intermedio' && nInt > 0) nInt -= 1;
          if (ex.type === 'especial') {
            extraItemsList.push({ name: `Poste especial ${ex.postType || ex.substType} ${ex.altCm}cm (ref. ${ex.tipoKey})`, ref: 'Poste especial',
              qty: 1, pu: ex.precio, sub: ex.precio, detail: `1 ud × ${ex.precio.toFixed(2)} € · ref. ${ex.tipoKey} (${ex.h}cm)` });
          } else {
            extraItemsList.push({ name: `Poste Esquinero ${td.h}cm`, ref: 'Esquinero',
              qty: 1, pu: ex.precio, sub: ex.precio, detail: `1 ud × ${ex.precio.toFixed(2)} €` });
          }
        });

        const nPost = nFin + nInt;
        totalMetros += t.metros;
        totalPostes += nPost;
        totalTramos += nT;

        const tItems = [];
        let tTotal   = 0;

        // Lamas del tramo
        const lamaSub = ldT.price * nLamas;
        tTotal += lamaSub;
        tItems.push({ name: `Lamas ${ldT.ref}`, ref: ldT.ref, qty: nLamas, pu: ldT.price, sub: lamaSub,
          detail: `${nLamas} uds × ${ldT.price.toFixed(2)} €` });

        // Postes del tramo (con cantidades ya ajustadas)
        const tPosteMat = t.posteMat || posteMat;
        const tPosteFix = t.posteFix || posteFix;
        calcPostes(td, nFin, nInt, 0, tPosteMat, tPosteFix).forEach(r => {
          tTotal += r.sub;
          tItems.push({ ...r, ref: r.name, detail: `${r.qty} uds × ${r.pu.toFixed(2)} €` });
        });

        // Añadir extras al tramo
        extraItemsList.forEach(ei => { tTotal += ei.sub; tItems.push(ei); });

        grandTotal += tTotal;
        const extCnt = (t.extras || []).length;
        const extLbl = extCnt > 0 ? `  ·  +${extCnt} extra${extCnt > 1 ? 's' : ''}` : '';
        sections.push({
          label: `Tramo ${ti+1}  ·  ${t.metros} m  ·  ${t.tipoKey} (${td.h} cm)  ·  ${sepLabel(sepT)} sep.  ·  ${(t.posteMat || posteMat) === 'aluminio' ? 'Aluminio' : 'PVC'}${extLbl}`,
          items: tItems,
          total: tTotal,
        });
        tItems.forEach(it => allItems.push({ ...it, name: `Tramo ${ti+1}: ${it.name}` }));
      });

      // Items globales (tapas, instalación) — separados de las secciones para mostrarlos aparte
      const globalItems = [];
      if (tapas > 0) {
        const tapSub = TAPA_PRICE * tapas;
        grandTotal += tapSub;
        const tapItem = { name: 'Tapa por poste', ref: 'Tapas', qty: tapas, pu: TAPA_PRICE, sub: tapSub,
          detail: `${tapas} uds × ${TAPA_PRICE.toFixed(2)} €` };
        globalItems.push(tapItem);
        allItems.push(tapItem);
      }
      if (inst > 0) {
        const instItem = { name: 'Instalación y mano de obra', ref: 'Instalación', qty: 1, pu: inst, sub: inst, detail: '' };
        grandTotal += inst;
        globalItems.push(instItem);
        allItems.push(instItem);
      }

      const tramoItems = validTramos.map(t => {
        const td  = tipos[t.tipoKey] || {};
        const ldT = lamas[t.lamaIdx] || lamas[0];
        const nT  = Math.ceil(t.metros / (ldT.sep || 2));
        return { metros: t.metros, tipoKey: t.tipoKey, tdT: td, ldT, nT, nPost: nT + 1,
          posteMat: t.posteMat || posteMat, posteFix: t.posteFix || posteFix,
          extras: t.extras || [] };
      });

      return {
        items: allItems, total: grandTotal, sections, globalItems,
        nTramos: totalTramos, nPostes: totalPostes, metros: totalMetros,
        modelData: currentProduct, instalacion: inst,
        modoTramos: true, tramoItems, specialItem: null,
      };
    }
  }, [model, tipo, lamaIdx, modo, metros, tramos, posteMat, posteFix, tapasQty, instalacion, esquin, finalesExtra, currentProduct, tipos, lamas, peSubst]);

  // Fusiona savedItems con tramos nuevos del cálculo automático.
  // Mantiene lo editado en tramos ya guardados, añade tramos nuevos al desglose
  // y descarta tramos que ya no existen.
  useEffect(() => {
    if (!result?.sections) return;
    setSavedItems(prev => {
      if (!prev) return prev;
      const savedSections = new Set(prev.map(it => it._section).filter(Boolean));
      const currentSections = new Set(result.sections.map(s => s.label));
      const merged = [];
      let changed = false;

      result.sections.forEach(sec => {
        if (savedSections.has(sec.label)) {
          merged.push(...prev.filter(it => it._section === sec.label));
        } else {
          sec.items.forEach(it => merged.push({ ...it, _section: sec.label }));
          changed = true;
        }
      });

      // Adicionales (instalación, tapas, etc.) siempre desde el cálculo —
      // son inputs propios del usuario y deben reflejar el valor actual.
      const prevAdic = prev.filter(it => it._section === 'Adicionales');
      const newAdic  = (result.globalItems || []).map(it => ({ ...it, _section: 'Adicionales' }));
      merged.push(...newAdic);
      if (prevAdic.length !== newAdic.length
          || prevAdic.some((p, i) => p.name !== newAdic[i].name || p.sub !== newAdic[i].sub)) {
        changed = true;
      }

      for (const sec of savedSections) {
        if (sec !== 'Adicionales' && !currentSections.has(sec)) { changed = true; break; }
      }

      return changed ? merged : prev;
    });
  }, [result]);

  // ── Descuento y comisión/margen ──────────────────────────────────────────
  const disc         = 1 - (descuento / 100);
  const displayItems = savedItems || editItems;
  const baseTotal    = displayItems
    ? displayItems.reduce((s, it) => s + (parseFloat(it.sub) || 0), 0)
    : result?.total || 0;
  const effectiveItems = displayItems || result?.items || [];
  const dtoManualNum   = parseFloat(dtoManualAmt) || 0;
  const extraCosteNum  = parseFloat(extraCosteAmt) || 0;
  const discTotal      = Math.max(0, baseTotal * disc - dtoManualNum + extraCosteNum);
  const ivaAmt       = discTotal * IVA_RATE;
  const totalConIva  = discTotal + ivaAmt;

  // Comisión para comercial, margen para admin/empresa
  const commRate   = isComercial
    ? (myRates.commission_pct != null ? myRates.commission_pct : (DISCOUNT_COMMISSION[descuento] || 5)) / 100
    : (myRates.margin_pct     != null ? myRates.margin_pct     : 0) / 100;
  const commission = discTotal * commRate;
  const commPct    = isComercial
    ? (myRates.commission_pct != null ? myRates.commission_pct : (DISCOUNT_COMMISSION[descuento] || 5))
    : (myRates.margin_pct     != null ? myRates.margin_pct     : 0);

  // ── Tramo handlers ───────────────────────────────────────────────────────
  function addTramo() {
    if (!currentProduct) return;
    const newId = tramoCount + 1;
    setTramoCount(newId);
    setTramos(prev => {
      const last = prev.length > 0 ? prev[prev.length - 1] : null;
      return [...prev, {
        id: newId,
        metros:   last ? last.metros   : '',
        tipoKey:  last ? last.tipoKey  : (Object.keys(tipos)[0] || ''),
        lamaIdx:  last ? last.lamaIdx  : 0,
        posteMat: last ? last.posteMat : posteMat,
        posteFix: last ? last.posteFix : (posteMat === 'aluminio' ? 'zapatilla' : posteFix),
        peAltura: last ? last.peAltura : '',
        extras:   [],
      }];
    });
  }
  function removeTramo(id) {
    setTramos(prev => prev.filter(t => t.id !== id));
  }
  function updateTramo(id, field, val) {
    setTramos(prev => prev.map(t => t.id === id ? { ...t, [field]: val } : t));
  }
  function setTramoPoste(id, mat) {
    setTramos(prev => prev.map(t => t.id === id
      ? { ...t, posteMat: mat, posteFix: mat === 'aluminio' ? 'zapatilla' : t.posteFix }
      : t));
  }
  function handleTramoPosteAlturaChange(id, val) {
    setTramos(prev => prev.map(t => t.id === id ? { ...t, peAltura: val } : t));
    const tramo = tramos.find(t => t.id === id);
    const n = parseInt(val);
    if (n > 0 && currentProduct && tramo) {
      const tPosteMat = tramo.posteMat || posteMat;
      const tPosteFix = tramo.posteFix || posteFix;
      const ref = findPosteEspecialRef(n, tipos, tPosteMat, tPosteFix);
      if (ref && ref.precio != null) setPeModalTramo({ tramoId: id, type: 'especial', altCm: n, ref });
    }
  }
  function openTramoEsquineroModal(tramoId) {
    const tramo = tramos.find(t => t.id === tramoId);
    if (!tramo) return;
    const td = tipos[tramo.tipoKey];
    if (!td) return;
    const precio = getEsquineroPrice(td, tramo.posteMat || posteMat, tramo.posteFix || posteFix);
    if (!precio) { showToast('No hay precio de esquinero para este tipo/material', 'error'); return; }
    setPeModalTramo({ tramoId, type: 'esquinero', esquineroPrice: precio });
  }
  function selectTramoPostType(pt) {
    setPeModalTramo(prev => ({ ...prev, step: 2, postType: pt }));
  }
  function confirmTramoExtra(substType) {
    if (!peModalTramo) return;
    const { tramoId, type, altCm, ref, esquineroPrice, postType } = peModalTramo;
    const extra = type === 'especial'
      ? { id: Date.now(), type: 'especial', postType: postType || substType, substType, altCm, tipoKey: ref.tipoKey, h: ref.tipoData.h, precio: ref.precio }
      : { id: Date.now(), type: 'esquinero', substType, precio: esquineroPrice };
    setTramos(prev => prev.map(t => t.id === tramoId
      ? { ...t, extras: [...(t.extras || []), extra], ...(type === 'especial' ? { peAltura: '' } : {}) }
      : t));
    setPeModalTramo(null);
  }
  function removeTramoExtra(tramoId, extraId) {
    setTramos(prev => prev.map(t => t.id === tramoId
      ? { ...t, extras: (t.extras || []).filter(ex => ex.id !== extraId) }
      : t));
  }
  // ── Editar materiales ─────────────────────────────────────────────────────
  function startEditMaterials() {
    if (!result) return;
    // Si ya hay items guardados, partir de ellos; si no, del resultado calculado
    if (savedItems) {
      setEditItems(savedItems.map((it, i) => ({ ...it, _key: i })));
    } else {
      let flat = [];
      if (result.sections) {
        result.sections.forEach(sec => {
          sec.items.forEach(it => flat.push({ ...it, _section: sec.label }));
        });
        (result.globalItems || []).forEach(it => flat.push({ ...it, _section: 'Adicionales' }));
      } else {
        flat = result.items.map(it => ({ ...it, _section: '' }));
      }
      setEditItems(flat.map((it, i) => ({ ...it, _key: i })));
    }
    setEditMode(true);
  }
  function saveEditMaterials() { setSavedItems(editItems); setEditMode(false); setEditItems(null); }
  function cancelEditMaterials() { setEditMode(false); setEditItems(null); }
  function updateEditItem(key, field, val) {
    setEditItems(prev => prev.map(it => {
      if (it._key !== key) return it;
      const u = { ...it, [field]: field === 'name' ? val : (parseFloat(val) || 0) };
      if (field === 'qty' || field === 'pu') {
        u.sub = u.qty * u.pu;
        u.detail = `${u.qty} uds × ${u.pu.toFixed(2)} €`;
      }
      return u;
    }));
  }
  function removeEditItem(key) {
    setEditItems(prev => prev.filter(it => it._key !== key));
  }
  function addEditItem() {
    setEditItems(prev => [...prev, { _key: Date.now(), name: 'Elemento personalizado', ref: '', qty: 1, pu: 0, sub: 0 }]);
  }
  function resetEditItems() { setSavedItems(null); setEditItems(null); setEditMode(false); }

  // ── Cambio de modelo con migración inteligente ───────────────────────────
  function changeModel(newModelKey) {
    if (newModelKey === model) return;
    if (!newModelKey) {
      setModel(''); setTipo(''); setLamaIdx(0); setTramos([]); setTramoCount(0); return;
    }
    const newProd  = productMap[newModelKey];
    const newTipos = newProd?.tipos || {};
    const newLamas = newProd?.lamas || [];
    const matchTipo = (oldKey) => {
      if (!oldKey) return Object.keys(newTipos)[0] || '';
      if (newTipos[oldKey]) return oldKey;
      const oldH = tipos[oldKey]?.h;
      if (oldH != null) {
        const sorted = Object.entries(newTipos)
          .sort((a, b) => Math.abs(a[1].h - oldH) - Math.abs(b[1].h - oldH));
        if (sorted.length) return sorted[0][0];
      }
      return Object.keys(newTipos)[0] || '';
    };
    const matchLama = (oldIdx) => {
      if (!newLamas.length) return 0;
      const oldSep = lamas[oldIdx]?.sep;
      if (oldSep != null) {
        const idx = newLamas.findIndex(l => l.sep === oldSep);
        if (idx >= 0) return idx;
      }
      return Math.min(oldIdx, newLamas.length - 1);
    };
    setTramos(prev => prev.map(t => ({
      ...t,
      tipoKey: matchTipo(t.tipoKey),
      lamaIdx: matchLama(t.lamaIdx),
      extras: [], peAltura: '',
    })));
    setTipo(matchTipo(tipo));
    setLamaIdx(matchLama(lamaIdx));
    setModel(newModelKey);
  }

  // ── Poste especial handlers ──────────────────────────────────────────────
  function handlePeAlturaChange(val) {
    setPeAltura(val);
    setPeSubst(null);
    const n = parseInt(val);
    if (n > 0 && currentProduct) {
      const ref = findPosteEspecialRef(n, tipos, posteMat, posteFix);
      if (ref && ref.precio != null) { setPeRef(ref); setPeModalStep(1); setPePostType(null); setPeModal(true); }
      else setPeRef(null);
    } else {
      setPeRef(null);
    }
  }

  function selectPePostType(pt) {
    setPePostType(pt);
    setPeModalStep(2);
  }

  function confirmPosteEspecial(substType) {
    if (!peRef) return;
    setPeSubst({ postType: pePostType || substType, substType, tipoKey: peRef.tipoKey, h: peRef.tipoData.h, precio: peRef.precio, altCm: parseInt(peAltura) });
    setPeModal(false); setPePostType(null); setPeModalStep(1);
  }

  function clearPosteEspecial() {
    setPeAltura(''); setPeSubst(null); setPeRef(null); setPeModal(false); setPePostType(null); setPeModalStep(1);
  }

  // ── Toast ────────────────────────────────────────────────────────────────
  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Guardar como lead (con deduplicación por teléfono) ───────────────────
  async function guardarComoPresupuesto() {
    if (!result) return;
    if (!cliente.tel?.trim()) { showToast('El teléfono del cliente es obligatorio', 'error'); return; }
    setSavingLead(true);
    try {
      // Limpiar HTML para guardar: quitar barra de impresión y script auto-print
      const cleanHTML = h => h
        .replace(/<div class="print-bar no-print">[\s\S]*?<\/div>/g, '')
        .replace(/<script>[\s\S]*?<\/script>/g, '');

      // ── Flujo CLIENTE: la proforma se asocia directamente al client_id (sin lead) ──
      if (cliente.client_id) {
        const freshSettings = await apiGet('/settings', auth.token);
        const freshCo = freshSettings.company_data || {};
        const contado = buildContadoPDFData(freshCo);
        const financ  = hasFinanciacion ? buildFinanciacionPDFData(freshCo) : null;
        if (contado) {
          await apiPost('/proformas', {
            client_id: cliente.client_id, tipo: 'contado', total: contado.total,
            titulo: contado.titulo, html: cleanHTML(contado.html),
          }, auth.token);
        }
        if (financ) {
          await apiPost('/proformas', {
            client_id: cliente.client_id, tipo: 'financiado', total: financ.total36,
            titulo: financ.titulo, html: cleanHTML(financ.html),
          }, auth.token);
        }
        showToast('✅ Presupuesto guardado en la ficha del cliente');
        return;
      }

      const budgetLines = result.items
        .map(it => `${it.name}: ${it.qty} uds × ${it.pu.toFixed(2)}€ = ${it.sub.toFixed(2)}€`)
        .join('\n');
      const budgetSummary = `Presupuesto calculadora:\n${budgetLines}\n\nBase: ${fmt(baseTotal)} | Dto: ${descuento}% | Total IVA: ${fmt(totalConIva)}`;

      let leadId, isNew, leadCode;
      if (cliente.lead_id) {
        // Vino desde un lead concreto (botón "Generar presupuesto" del lead): usar SU id.
        // Imprescindible cuando hay leads duplicados con el mismo teléfono: buscar por
        // teléfono podría adjuntar la proforma al lead gemelo equivocado y "no se guardaría".
        leadId   = cliente.lead_id;
        leadCode = cliente.leadCode || '';
        isNew    = false;
        console.log('[CALC] Usando lead_id explícito del lead de origen:', leadId);
      } else {
        // Buscar lead existente por teléfono (único criterio: últimos 9 dígitos)
        const last9 = s => s.replace(/\D/g, '').slice(-9);
        const telNorm = last9(cliente.tel);
        console.log('[CALC] Buscando lead por últimos 9 dígitos:', telNorm);
        const leads = await apiGet(`/leads?search=${encodeURIComponent(telNorm)}`, auth.token);
        console.log('[CALC] Leads encontrados:', Array.isArray(leads) ? leads.length : leads);
        const existing = Array.isArray(leads)
          ? leads.find(l => last9(l.phone || '') === telNorm)
          : null;
        console.log('[CALC] Lead existente:', existing ? existing.id : 'ninguno');

        if (existing) {
          leadId   = existing.id;
          leadCode = existing.leadCode;
          isNew    = false;
        } else {
          if (!cliente.nombre?.trim()) { showToast('El nombre del cliente es obligatorio para crear un lead nuevo', 'error'); setSavingLead(false); return; }
          console.log('[CALC] Creando nuevo lead con teléfono:', telNorm);
          try {
            const data = await apiPost('/leads', {
              name:     cliente.nombre.trim(),
              phone:    telNorm,
              email:    cliente.email,
              city:     cliente.poblacion,
              source:   'calculadora',
              comments: budgetSummary,
            }, auth.token);
            console.log('[CALC] Lead creado:', data);
            leadId   = data.id;
            leadCode = data.leadCode;
            isNew    = true;
          } catch (e) {
            // 409: el lead ya existe en el tenant (asignado a otro comercial) → adjuntar presupuesto a él
            if (e.status === 409 && e.data?.existingId) {
              console.log('[CALC] Lead ya existía en tenant, usando existente:', e.data.leadCode);
              leadId   = e.data.existingId;
              leadCode = e.data.leadCode;
              isNew    = false;
            } else {
              throw e;
            }
          }
        }
      }

      // Cargar datos frescos del tenant para el pie de presupuesto
      const freshSettings = await apiGet('/settings', auth.token);
      const freshCo = freshSettings.company_data || {};
      // Guardar proformas (contado + financiado si aplica) en BD con el HTML completo
      const contado  = buildContadoPDFData(freshCo);
      const financ   = hasFinanciacion ? buildFinanciacionPDFData(freshCo) : null;
      if (contado) {
        const cleanedC = cleanHTML(contado.html);
        console.log('[CALC] Proforma contado size:', (cleanedC.length/1024).toFixed(1), 'KB');
        const r1 = await apiPost('/proformas', {
          lead_id: leadId, tipo: 'contado', total: contado.total,
          titulo: contado.titulo, html: cleanedC,
        }, auth.token);
      }
      if (financ) {
        const cleanedF = cleanHTML(financ.html);
        console.log('[CALC] Proforma financiado size:', (cleanedF.length/1024).toFixed(1), 'KB');
        const r2 = await apiPost('/proformas', {
          lead_id: leadId, tipo: 'financiado', total: financ.total36,
          titulo: financ.titulo, html: cleanedF,
        }, auth.token);
      }

      setSavedLeadId(leadId);
      showToast(isNew
        ? `✅ Lead creado: ${leadCode} · estado → seguimiento`
        : `✅ Presupuesto añadido a ${leadCode} · estado → seguimiento`
      );
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingLead(false);
    }
  }

  // ── Guardar PDF en lead (sin descarga) ───────────────────────────────────
  function buildPDFNoteContent(type, profoNum, fecha) {
    const isContado = type === 'contado';
    const { recFactor } = getFinCfg();
    const base  = Math.max(0, (isContado ? result.total * disc : result.total * disc * recFactor) - dtoManualNum + extraCosteNum);
    const iva   = base * IVA_RATE;
    const total = base + iva;
    const label = isContado ? 'CONTADO' : 'FINANCIACIÓN BBVA';
    const lines = result.items.map(it => {
      const pu  = isContado ? it.pu  * disc : it.pu  * disc * recFactor;
      const sub = isContado ? it.sub * disc : it.sub * disc * recFactor;
      return `• ${it.name}: ${it.qty} uds × ${pu.toFixed(2)} € = ${fmt(sub)}`;
    }).join('\n');
    return `Ref: ${profoNum} · ${fecha}\nModalidad: ${label}\n\n${lines}\n\nBase: ${fmt(base)} | IVA 21%: ${fmt(iva)} | TOTAL: ${fmt(total)}`;
  }

  async function savePDFToLead(type) {
    if (!result) return;
    if (!cliente.tel) { showToast('El teléfono del cliente es obligatorio para guardar el PDF', 'error'); return; }
    setSavingPDF(true);
    try {
      const { profoNum, fecha } = makeProfoMeta();
      let noteContent = '';
      if (type === 'ambos') {
        noteContent = buildPDFNoteContent('contado', profoNum, fecha)
          + '\n\n──────────────────────\n\n'
          + buildPDFNoteContent('financiado', profoNum, fecha);
      } else {
        noteContent = buildPDFNoteContent(type, profoNum, fecha);
      }

      let leadId, isNew;
      if (cliente.lead_id) {
        // Vino desde un lead concreto: usar SU id (evita adjuntarlo a un lead duplicado).
        leadId = cliente.lead_id;
        isNew  = false;
      } else {
        // Buscar lead existente por teléfono
        const telNorm = cliente.tel.replace(/\s+/g, '');
        const leads   = await apiGet(`/leads?search=${encodeURIComponent(cliente.tel)}`, auth.token);
        const existing = Array.isArray(leads)
          ? leads.find(l => (l.phone || '').replace(/\s+/g, '') === telNorm)
          : null;
        if (existing) {
          leadId = existing.id;
          isNew  = false;
        } else {
          try {
            const data = await apiPost('/leads', {
              name:   cliente.nombre || 'Cliente',
              phone:  cliente.tel,
              email:  cliente.email,
              city:   cliente.poblacion,
              source: 'calculadora',
            }, auth.token);
            leadId = data.id;
            isNew  = true;
          } catch (e) {
            if (e.status === 409 && e.data?.existingId) { leadId = e.data.existingId; isNew = false; }
            else throw e;
          }
        }
      }

      await apiPost('/notes', {
        entity_type: 'lead',
        entity_id:   leadId,
        content:     `📄 PDF ${type} guardado desde calculadora:\n\n${noteContent}`,
      }, auth.token);

      showToast(isNew
        ? `✅ PDF guardado en nuevo lead`
        : `✅ PDF guardado en lead existente`
      );
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingPDF(false);
    }
  }

  function downloadPDF(type) {
    if (type === 'ambos') {
      // Fetch settings una vez y descargar ambos con <a download> (no popup blocker)
      apiGet('/settings', auth.token).then(s => {
        const freshCo = s.company_data || {};
        const contado = buildContadoPDFData(freshCo);
        const financ  = buildFinanciacionPDFData(freshCo);
        if (contado) downloadBlob(contado.html, contado.titulo);
        if (financ)  setTimeout(() => downloadBlob(financ.html, financ.titulo), 500);
      }).catch(e => alert('Error al generar PDFs: ' + e.message));
      return;
    }
    if (type === 'contado')    generateContadoPDF();
    if (type === 'financiado') generateFinanciacionPDF();
  }

  // ── PDF helpers ──────────────────────────────────────────────────────────
  function makeProfoMeta() {
    const now      = new Date();
    const rand     = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
    const profoNum = `PRF-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}-${rand}`;
    const fecha    = now.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
    const validez  = new Date(now.getTime() + 30*24*60*60*1000).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
    const fixLabel = { zapatilla: 'con zapatilla', tubo: 'con tubo', sin: 'sin zapatilla' };
    const posteLabel = posteMat === 'aluminio' ? 'Aluminio (con zapatilla)' : `PVC (${fixLabel[posteFix] || posteFix})`;
    return { profoNum, fecha, validez, posteLabel };
  }

  function buildHeaderHTML(profoNum, fecha, validez, posteLabel) {
    if (!result) return '';
    const cn = cliente.nombre    || 'Cliente';
    const cp = cliente.poblacion || '';
    const ct = cliente.tel       || '';
    const ce = cliente.email     || '';

    const configSection = result.modoTramos ? `
    <div class="pf-config-box" style="flex-direction:column;gap:0.75rem">
      <div style="display:flex;gap:2rem;flex-wrap:wrap">
        <div><div class="ci-label">Modelo</div><div class="ci-val">${result.modelData.fullName}</div></div>
        ${isColorModel ? `<div><div class="ci-label">Color</div><div class="ci-val" style="font-weight:700">${selectedColor}</div></div>` : ''}
        <div><div class="ci-label">Tipo de poste</div><div class="ci-val">${posteLabel}</div></div>
        <div><div class="ci-label">Total metros</div><div class="ci-val">${result.metros.toFixed(2)} m</div></div>
        <div><div class="ci-label">Total postes</div><div class="ci-val">${result.nPostes} uds</div></div>
      </div>
      <div style="border-top:1px solid #E0E0DC;padding-top:0.6rem">
        <div class="ci-label" style="margin-bottom:0.4rem">Detalle por tramo</div>
        ${result.tramoItems.map((tr, i) => `
        <div style="display:flex;gap:1.5rem;font-size:0.78rem;flex-wrap:wrap;background:#fff;border-radius:5px;padding:0.3rem 0.6rem;margin-bottom:3px">
          <span style="font-weight:700;color:${brand};min-width:60px">Tramo ${i+1}</span>
          <span><strong>${tr.metros} m</strong> lineales</span>
          <span><strong>${tr.tipoKey}</strong> · ${tr.tdT.h || '—'} cm</span>
          <span>${sepLabel(tr.ldT.sep)} sep.</span>
          <span>${tr.nT} tramos · ${tr.nPost} postes</span>
          <span>Poste: <strong>${tr.posteMat === 'aluminio' ? 'Aluminio' : 'PVC'}</strong></span>
          ${(tr.extras||[]).map(ex => ex.type==='esquinero' ? '<span>+Esq.</span>' : `<span>+PE ${ex.altCm}cm</span>`).join('')}
        </div>`).join('')}
      </div>
    </div>` : `
    <div class="pf-config-box">
      <div><div class="ci-label">Modelo</div><div class="ci-val">${result.modelData.fullName}</div></div>
      ${isColorModel ? `<div><div class="ci-label">Color</div><div class="ci-val" style="font-weight:700">${selectedColor}</div></div>` : ''}
      <div><div class="ci-label">Tipo / Altura</div><div class="ci-val">${result.tipo} · ${result.tipoData?.h || '—'} cm</div></div>
      <div><div class="ci-label">Metros lineales</div><div class="ci-val">${result.metros} m</div></div>
      <div><div class="ci-label">Separación</div><div class="ci-val">${sepLabel(result.sep)}</div></div>
      <div><div class="ci-label">Tipo de poste</div><div class="ci-val">${posteLabel}</div></div>
    </div>`;

    const emisorDetail = [
      tenantCompany.nif       ? tenantCompany.nif       : '',
      tenantCompany.direccion ? tenantCompany.direccion : '',
      tenantCompany.telefono  ? '📞 ' + tenantCompany.telefono : '',
      tenantCompany.email     ? '✉ '  + tenantCompany.email    : '',
    ].filter(Boolean).join('<br>');

    return `
    <div class="pf-header">
      <div>
        ${tenantLogo
          ? `<img src="${tenantLogo}" style="max-height:54px;max-width:160px;object-fit:contain;display:block;margin-bottom:4px" />`
          : `<div style="font-family:'DM Sans',sans-serif;font-size:22px;font-weight:800;color:#1A1A1A;letter-spacing:0.02em;line-height:1">${tenantName}</div>`
        }
      </div>
      <div class="pf-doc-info">
        <div class="pf-doc-num">${profoNum}</div>
        <div class="pf-doc-date">${fecha}</div>
        <span class="pf-doc-label">Proforma de presupuesto</span>
      </div>
    </div>
    <div class="pf-divider"></div>
    <div class="pf-client-grid">
      <div>
        <div class="pf-client-label">Cliente</div>
        <div class="pf-client-name">${cn}</div>
        <div class="pf-client-detail">${cp ? cp + '<br>' : ''}${ct ? '📞 ' + ct + '&nbsp;&nbsp;' : ''}${ce ? '✉ ' + ce : ''}</div>
      </div>
      <div>
        <div class="pf-client-label">Empresa emisora</div>
        <div class="pf-client-name" style="font-size:0.95rem">${tenantCompany.nombre || tenantName}</div>
        <div class="pf-client-detail">${emisorDetail}</div>
      </div>
    </div>
    ${configSection}`;
  }

  // ── PDF Contado — builder ────────────────────────────────────────────────
  // Helper: genera HTML de pie de presupuesto desde quote_footer del tenant
  function buildQuoteFooterHTML(brand, { incluyeInstalacion = false, validez = '', freshCompany = null } = {}) {
    const qf = (freshCompany || tenantCompany).quote_footer || {};

    // Condiciones (texto diferente según incluya o no instalación)
    const defaultCon = `Presupuesto PVP ${new Date().getFullYear()}. ${incluyeInstalacion ? 'Incluye instalación y mano de obra.' : 'No incluye mano de obra ni instalación.'} Válido hasta el ${validez}. Documento orientativo; presupuesto definitivo confirmado por el departamento comercial.`;
    const condTxt = incluyeInstalacion
      ? (qf.condiciones || defaultCon)
      : (qf.condiciones_sin_instalacion || defaultCon);
    const condHTML = `<div class="pf-notes" style="background:#FFF3EE;border-left:3px solid ${brand}">
      <div class="pf-notes-title" style="color:${brand}">Condiciones</div>
      ${incluyeInstalacion ? '<p><strong style="color:#2E7D32">✓ INSTALACIÓN Y MANO DE OBRA INCLUIDA</strong></p>' : ''}
      <p>${condTxt}</p>
    </div>`;

    // Forma de pago
    const pagoTxt = qf.forma_pago || '';
    const pagoLines = pagoTxt ? pagoTxt.split('\n').filter(l => l.trim()) : [];
    const pagoListHTML = pagoLines.length > 0
      ? `<ul style="margin:4px 0 6px 16px;padding:0;font-size:10px;color:#333;line-height:1.75">${pagoLines.map(l => `<li>${l.replace(/^[•\-]\s*/, '')}</li>`).join('')}</ul>`
      : '';

    // Datos bancarios
    const iban = qf.cuenta_iban || '';
    const titular = qf.cuenta_nombre || '';
    const cuentaHTML = iban
      ? `<p style="font-size:10px;color:#555;margin-top:4px">Número de cuenta para transferencias:<br><strong style="font-family:monospace;letter-spacing:.05em;font-size:11px">${iban}</strong>${titular ? ` &nbsp;· a nombre de: <strong>${titular}</strong>` : ''}</p>`
      : '';

    const pagoHTML = (pagoListHTML || cuentaHTML) ? `<div class="pf-notes" style="background:#EEF4FF;border-left:3px solid #003087">
      <div class="pf-notes-title" style="color:#003087">Forma de pago</div>
      ${pagoListHTML}${cuentaHTML}
    </div>` : '';

    return condHTML + pagoHTML;
  }

  function buildContadoPDFData(freshCompany) {
    if (!result) return null;
    const { profoNum, fecha, validez, posteLabel } = makeProfoMeta();
    const { ficticioPct } = getFinCfg(freshCompany);
    // Precio de cada línea = NORMAL (grande), con el precio de financiación (+% ficticio) tachado en pequeño.
    // El dto MANUAL NO se aplica por línea: solo se refleja en el total (sumado al ficticio → p.ej. 15%).
    const lineCross      = 1 + ficticioPct / 100;                 // precio con financiación por línea (solo ficticio)
    const showComparison = (ficticioPct + (Number(descuento) || 0)) > 0;
    const shownPct       = ficticioPct + (Number(descuento) || 0); // descuento total mostrado (ficticio + manual)
    // El coste adicional queda EXENTO del dto manual (va a precio íntegro en el total)
    const base    = Math.max(0, baseTotal * disc - dtoManualNum + extraCosteNum);
    const iva     = base * IVA_RATE;
    const total   = base + iva;
    // Precio "con financiación" (tachado global) = líneas normales + coste adicional, inflado por el ficticio
    const total36 = (baseTotal + extraCosteNum) * lineCross * (1 + IVA_RATE);
    const tableRows = effectiveItems.map((it, i) => {
      const puCell  = showComparison
        ? `<div style="color:#bbb;text-decoration:line-through;font-size:0.78em">${(it.pu*lineCross).toFixed(2)} €</div><div style="font-weight:700">${it.pu.toFixed(2)} €</div>`
        : `<div style="font-weight:700">${it.pu.toFixed(2)} €</div>`;
      const subCell = showComparison
        ? `<div style="color:#bbb;text-decoration:line-through;font-size:0.78em">${fmt(it.sub*lineCross)}</div><div style="font-weight:700">${fmt(it.sub)}</div>`
        : `<div style="font-weight:700">${fmt(it.sub)}</div>`;
      return `<tr>
        <td>${String(i+1).padStart(2,'0')}</td><td>${it.name}</td>
        <td style="text-align:right">${it.qty}</td>
        <td style="text-align:right">${puCell}</td>
        <td style="text-align:right">${subCell}</td>
      </tr>`;
    }).join('');
    const extraRow = (extraCosteNum > 0 || (extraCosteConcepto && extraCosteConcepto.trim())) ? `<tr style="background:#EEF4FF">
      <td>${String(effectiveItems.length+1).padStart(2,'0')}</td><td style="color:#1565C0;font-weight:600">${extraCosteConcepto || 'Coste adicional'}</td>
      <td style="text-align:right">1</td>
      <td style="text-align:right">${showComparison ? `<div style="color:#bbb;text-decoration:line-through;font-size:0.78em">${(extraCosteNum*lineCross).toFixed(2)} €</div>` : ''}<div style="font-weight:700">${extraCosteNum.toFixed(2)} €</div></td>
      <td style="text-align:right">${showComparison ? `<div style="color:#bbb;text-decoration:line-through;font-size:0.78em">${fmt(extraCosteNum*lineCross)}</div>` : ''}<div style="font-weight:700;color:#1565C0">${fmt(extraCosteNum)}</div></td>
    </tr>` : '';
    const sheetHTML = buildHeaderHTML(profoNum, fecha, validez, posteLabel) + `
    <table>
      <thead><tr><th>#</th><th>Descripción</th><th style="text-align:right">Cant.</th><th style="text-align:right">P. unitario</th><th style="text-align:right">Subtotal</th></tr></thead>
      <tbody>${tableRows}${extraRow}</tbody>
    </table>
    ${showComparison ? `<div class="pf-contado-wrap">
      <div class="pf-contado-left">
        <div class="pf-contado-badge">${hasFinanciacion ? 'Precio con financiación' : 'Precio de tarifa'}</div>
        <div class="pf-contado-was">${fmt(total36)}</div>
        <div style="font-size:9px;color:#999;margin-top:2px">IVA incluido</div>
      </div>
      <div class="pf-contado-arrow">→</div>
      <div class="pf-contado-right">
        <div class="pf-contado-badge ok">✓ Precio al contado</div>
        <div class="pf-contado-price">${fmt(total)}</div>
        <div style="font-size:9px;color:#888;margin-top:2px">IVA incluido · Pago único</div>
        <div class="pf-contado-saving">Ahorro ${shownPct}%: ${fmt(total36 - total)}</div>
      </div>
    </div>` : ''}
    <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
      <div style="min-width:260px">
        ${showComparison ? `<div class="pf-total-row" style="color:#B83000"><span>Descuento aplicado (${shownPct}%)</span><span>−${fmt(total36 - total)}</span></div>` : ''}
        ${dtoManualNum > 0 ? `<div class="pf-total-row" style="color:#2E7D32;font-weight:600"><span>${dtoManualConcepto || 'Descuento manual'}</span><span>−${fmt(dtoManualNum)}</span></div>` : ''}
        <div class="pf-total-row"><span>Base imponible</span><span>${fmt(base)}</span></div>
        <div class="pf-total-row"><span>IVA 21%</span><span>${fmt(iva)}</span></div>
        <div class="pf-total-row main-total"><span>TOTAL AL CONTADO</span><span>${fmt(total)}</span></div>
      </div>
    </div>
    ${buildQuoteFooterHTML(brand, { incluyeInstalacion: result.instalacion > 0, validez, freshCompany })}
    <div class="pf-footer">
      <span>${(freshCompany||tenantCompany).nombre || tenantName}</span>
      <span>Ref. ${profoNum} · ${fecha}</span>
    </div>`;
    const safeName = (cliente.nombre || 'Cliente').replace(/[^a-zA-Z0-9]/g, '_');
    const safeTel  = (cliente.tel || '').replace(/[^0-9]/g, '');
    const titulo = `Presupuesto_Contado_${safeName}_${safeTel}`;
    return { html: buildPDFShell(titulo, sheetHTML, brand), titulo, total, profoNum };
  }

  async function generateContadoPDF() {
    const s = await apiGet('/settings', auth.token);
    const d = buildContadoPDFData(s.company_data || {});
    if (d) openBlob(d.html, d.titulo);
  }

  // ── PDF Financiación — builder ────────────────────────────────────────────
  function buildFinanciacionPDFData(freshCompany) {
    if (!result) return null;
    const { profoNum, fecha, validez, posteLabel } = makeProfoMeta();
    const { recFactor, meses } = getFinCfg(freshCompany);
    const base    = Math.max(0, baseTotal * disc - dtoManualNum + extraCosteNum);
    // El coste adicional (transporte, grúa…) queda EXENTO del recargo de financiación
    const base36  = (base - extraCosteNum) * recFactor + extraCosteNum;
    const iva36   = base36 * IVA_RATE;
    const total36 = base36 + iva36;
    const cuota   = total36 / meses;
    const tableRows = effectiveItems.map((it, i) => {
      const pu  = it.pu  * disc * recFactor;
      const sub = it.sub * disc * recFactor;
      return `<tr>
        <td>${String(i+1).padStart(2,'0')}</td><td>${it.name}</td>
        <td style="text-align:right">${it.qty}</td>
        <td style="text-align:right">${pu.toFixed(2)} €</td>
        <td style="text-align:right;font-weight:600">${fmt(sub)}</td>
      </tr>`;
    }).join('');
    const extraRow2 = (extraCosteNum > 0 || (extraCosteConcepto && extraCosteConcepto.trim())) ? `<tr style="background:#EEF4FF">
      <td>${String(effectiveItems.length+1).padStart(2,'0')}</td><td style="color:#1565C0;font-weight:600">${extraCosteConcepto || 'Coste adicional'}</td>
      <td style="text-align:right">1</td>
      <td style="text-align:right">${extraCosteNum.toFixed(2)} €</td>
      <td style="text-align:right;font-weight:600;color:#1565C0">${fmt(extraCosteNum)}</td>
    </tr>` : '';
    const sheetHTML = buildHeaderHTML(profoNum, fecha, validez, posteLabel) + `
    <div class="pf-hero-financ">
      <div class="pf-hero-top">
        <div>
          <span class="pf-hero-bbva">BBVA</span>
          <span class="pf-hero-tagline">Financiación sin intereses</span>
        </div>
        <div class="pf-hero-zero">0%</div>
      </div>
      <div class="pf-hero-cuota-label">Tu cuota mensual</div>
      <div class="pf-hero-cuota">${cuota.toFixed(2).replace('.', ',')} <span>€/mes</span></div>
      <div class="pf-hero-pills">
        <span class="pill">${meses} cuotas</span>
        <span class="pill">0% interés</span>
        <span class="pill">Sin comisión apertura</span>
        <span class="pill">TIN 0% · TAE 0%</span>
      </div>
      <div class="pf-hero-total">Total financiado: <strong>${fmt(total36)}</strong> IVA incl. · Financiación gestionada con BBVA · Sujeto a aprobación bancaria</div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Descripción del material</th><th style="text-align:right">Cant.</th><th style="text-align:right">P. unitario</th><th style="text-align:right">Subtotal</th></tr></thead>
      <tbody>${tableRows}${extraRow2}</tbody>
    </table>
    <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
      <div style="min-width:260px">
        ${descuento > 0 ? `<div class="pf-total-row" style="color:#B83000"><span>Descuento aplicado (${descuento}%)</span><span>−${fmt(result.total * recFactor * (descuento/100))}</span></div>` : ''}
        ${dtoManualNum > 0 ? `<div class="pf-total-row" style="color:#2E7D32;font-weight:600"><span>${dtoManualConcepto || 'Descuento manual'}</span><span>−${fmt(dtoManualNum)}</span></div>` : ''}
        <div class="pf-total-row"><span>Base imponible</span><span>${fmt(base36)}</span></div>
        <div class="pf-total-row"><span>IVA 21%</span><span>${fmt(iva36)}</span></div>
        <div class="pf-total-row main-total"><span>TOTAL FINANCIADO</span><span>${fmt(total36)}</span></div>
      </div>
    </div>
    ${buildQuoteFooterHTML(brand, { incluyeInstalacion: result.instalacion > 0, validez, freshCompany })}
    <div class="pf-footer">
      <span>${(freshCompany||tenantCompany).nombre || tenantName}</span>
      <span>Ref. ${profoNum} · ${fecha}</span>
    </div>`;
    const safeName = (cliente.nombre || 'Cliente').replace(/[^a-zA-Z0-9]/g, '_');
    const safeTel  = (cliente.tel || '').replace(/[^0-9]/g, '');
    const titulo = `Presupuesto_Financiado_${safeName}_${safeTel}`;
    return { html: buildPDFShell(titulo, sheetHTML, brand), titulo, total36, profoNum };
  }

  async function generateFinanciacionPDF() {
    const s = await apiGet('/settings', auth.token);
    const d = buildFinanciacionPDFData(s.company_data || {});
    if (d) openBlob(d.html, d.titulo);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return <div className="calc-loading">Cargando calculadora…</div>;

  const zapatillaDisabled = posteMat === 'pvc' && tipo && tipos[tipo] && !tipos[tipo].pzap;

  return (
    <div className="calc-page">
      {/* Banner */}
      <div className="calc-banner">
        <div>
          <div className="calc-banner-title">Calculadora de Vallas PVC</div>
          <div className="calc-banner-sub">Calcula materiales, precios y genera proformas</div>
        </div>
        <div className="calc-banner-pills">
          <span className="calc-pill">PVC · Aluminio</span>
          <span className="calc-pill">IVA 21% incluido</span>
          <span className="calc-pill">Proforma PDF</span>
        </div>
      </div>

      {/* Toast */}
      {toast && <div className={`calc-toast calc-toast--${toast.type}`}>{toast.msg}</div>}

      <div className="calc-layout">
        {/* ─── COLUMNA IZQUIERDA ─── */}
        <div className={`calc-left${editMode ? ' calc-left--locked' : ''}`}>

          {/* Panel 1 — Modelo y tipo */}
          <div className="calc-panel">
            <div className="calc-panel-header">
              <span className="cp-num">1</span>Modelo y tipo
            </div>
            <div className="calc-panel-body">
              <div className="cf-row">
                <div className="cf-field">
                  <label>Modelo de valla</label>
                  <select value={model} onChange={e => changeModel(e.target.value)}>
                    <option value="">— Elige modelo —</option>
                    {products.map(p => (
                      <option key={p.product_key} value={p.product_key}>{p.product_name}</option>
                    ))}
                  </select>
                </div>
                <div className="cf-field">
                  <label>Separación / Lama</label>
                  <select value={lamaIdx} onChange={e => setLamaIdx(parseInt(e.target.value))} disabled={!model}>
                    {lamas.map((l, i) => (
                      <option key={i} value={i}>{sepLabel(l.sep)} · {l.ref} · {l.price.toFixed(2)} €/ud</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Selector de color (solo modelos _color) */}
              {isColorModel && (
                <div className="cf-row">
                  <div className="cf-field cf-field--full">
                    <label>Color</label>
                    <div className="color-pills color-pills--sm">
                      {['Nogal', 'Roble', 'Gris Antracita'].map(c => (
                        <button
                          key={c}
                          className={`color-pill${selectedColor === c ? ' active' : ''}`}
                          onClick={() => setSelectedColor(c)}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Panel 2 — Modo de cálculo */}
          <div className="calc-panel">
            <div className="calc-panel-header">
              <span className="cp-num">2</span>Modo de cálculo
            </div>
            <div className="calc-panel-body">
              <div className="modo-toggle">
                <button
                  className={`modo-btn${modo === 'metros' ? ' active' : ''}`}
                  onClick={() => setModo('metros')}
                >
                  📏 Por metros totales
                </button>
                <button
                  className={`modo-btn${modo === 'tramos' ? ' active' : ''}`}
                  onClick={() => {
                    setModo('tramos');
                    if (tramos.length === 0 && model) addTramo();
                  }}
                >
                  ✂️ Por tramos
                </button>
              </div>

              {modo === 'metros' && (
                <div className="cf-row">
                  <div className="cf-field" style={{ marginBottom: 0 }}>
                    <label>Metros lineales totales</label>
                    <input
                      type="number"
                      value={metros}
                      onChange={e => setMetros(e.target.value)}
                      placeholder="Ej: 25.5"
                      min="0" step="any"
                    />
                  </div>
                  <div className="cf-field" style={{ marginBottom: 0 }}>
                    <label>Altura (Tipo T)</label>
                    <select value={tipo} onChange={e => setTipo(e.target.value)} disabled={!model}>
                      <option value="">— Elige modelo primero —</option>
                      {Object.entries(tipos).map(([k, v]) => (
                        <option key={k} value={k}>{k} · {v.h} cm</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {modo === 'tramos' && (
                <div>
                  {tramos.map((t, idx) => (
                    <div key={t.id} className="tramo-card">
                      <div className="tramo-card-header">
                        <span className="tramo-num">Tramo {idx + 1}</span>
                        <button className="btn-del-tramo" onClick={() => removeTramo(t.id)}>✕</button>
                      </div>
                      <div className="cf-row cf-row3">
                        <div className="cf-field" style={{ marginBottom: 0 }}>
                          <label>Metros</label>
                          <input
                            type="number"
                            value={t.metros}
                            onChange={e => updateTramo(t.id, 'metros', parseFloat(e.target.value) || '')}
                            placeholder="Ej: 10"
                            min="0" step="any"
                          />
                        </div>
                        <div className="cf-field" style={{ marginBottom: 0 }}>
                          <label>Tipo T</label>
                          <select value={t.tipoKey} onChange={e => updateTramo(t.id, 'tipoKey', e.target.value)}>
                            {Object.entries(tipos).map(([k, v]) => (
                              <option key={k} value={k}>{k} · {v.h} cm</option>
                            ))}
                          </select>
                        </div>
                        <div className="cf-field" style={{ marginBottom: 0 }}>
                          <label>Lama</label>
                          <select value={t.lamaIdx} onChange={e => updateTramo(t.id, 'lamaIdx', parseInt(e.target.value))}>
                            {lamas.map((l, i) => (
                              <option key={i} value={i}>{sepLabel(l.sep)} · {l.price.toFixed(2)} €</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="cf-row" style={{ marginTop: '0.4rem' }}>
                        <div className="cf-field" style={{ marginBottom: 0 }}>
                          <label>Poste</label>
                          <select value={t.posteMat || 'pvc'} onChange={e => setTramoPoste(t.id, e.target.value)}>
                            <option value="pvc">PVC</option>
                            <option value="aluminio">Aluminio</option>
                          </select>
                        </div>
                        <div className="cf-field" style={{ marginBottom: 0 }}>
                          <label>Fijación</label>
                          <select
                            value={t.posteFix || 'zapatilla'}
                            onChange={e => updateTramo(t.id, 'posteFix', e.target.value)}
                            disabled={(t.posteMat || 'pvc') === 'aluminio'}
                          >
                            <option value="zapatilla"
                              disabled={(t.posteMat || 'pvc') === 'pvc' && t.tipoKey && tipos[t.tipoKey] && !tipos[t.tipoKey].pzap}>
                              Con zapatilla
                            </option>
                            <option value="tubo">Con tubo</option>
                            <option value="sin">Sin zapatilla</option>
                          </select>
                        </div>
                      </div>
                      {/* Extras: postes especiales y esquineros */}
                      <div style={{ marginTop: '0.4rem' }}>
                        {(t.extras || []).map(ex => (
                          <div key={ex.id} className="pe-badge" style={{ marginBottom: '0.3rem' }}>
                            <span>
                              {ex.type === 'esquinero'
                                ? <>Esq. → sustituye <strong>{ex.substType}</strong> · <strong>{ex.precio.toFixed(2)} €/ud</strong></>
                                : <>PE <strong>{ex.altCm}cm</strong> → <strong>{ex.tipoKey}</strong> · <strong>{ex.substType}</strong> · <strong>{ex.precio.toFixed(2)} €/ud</strong></>
                              }
                            </span>
                            <button className="pe-badge-clear" onClick={() => removeTramoExtra(t.id, ex.id)}>✕</button>
                          </div>
                        ))}
                        <div className="cf-row">
                          <div className="cf-field" style={{ marginBottom: 0 }}>
                            <label>Poste especial (cm)</label>
                            <input
                              type="number"
                              value={t.peAltura || ''}
                              onChange={e => handleTramoPosteAlturaChange(t.id, e.target.value)}
                              placeholder="Altura cm"
                              min="1" step="1"
                            />
                          </div>
                          <div className="cf-field" style={{ marginBottom: 0 }}>
                            <label>&nbsp;</label>
                            <button
                              className="btn-add-tramo"
                              style={{ width: '100%' }}
                              onClick={() => openTramoEsquineroModal(t.id)}
                              disabled={!tipos[t.tipoKey]}
                            >
                              + Esquinero
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button className="btn-add-tramo" onClick={addTramo} disabled={!model}>
                    + Añadir tramo
                  </button>
                </div>
              )}

              {result && (
                <div className="cf-row" style={{ marginTop: '0.75rem' }}>
                  <div className="cf-field" style={{ marginBottom: 0 }}>
                    <label>Nº de tramos</label>
                    <input readOnly className="cf-auto" value={result.nTramos} />
                  </div>
                  <div className="cf-field" style={{ marginBottom: 0 }}>
                    <label>Nº de postes</label>
                    <input readOnly className="cf-auto" value={result.nPostes} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Panel 3 — Tipo de poste */}
          <div className="calc-panel">
            <div className="calc-panel-header">
              <span className="cp-num">3</span>Tipo de poste
            </div>
            <div className="calc-panel-body">
              <div className="cf-row">
                <div className="cf-field">
                  <label>Material</label>
                  <select value={posteMat} onChange={e => setPosteMat(e.target.value)}>
                    <option value="pvc">PVC</option>
                    <option value="aluminio">Aluminio</option>
                  </select>
                </div>
                <div className="cf-field">
                  <label>Fijación</label>
                  <select value={posteFix} onChange={e => setPosteFix(e.target.value)} disabled={posteMat === 'aluminio'}>
                    <option value="zapatilla" disabled={zapatillaDisabled}>
                      Con zapatilla{zapatillaDisabled ? ' (no disponible)' : ''}
                    </option>
                    <option value="tubo">Con tubo</option>
                    <option value="sin">Sin zapatilla</option>
                  </select>
                </div>
              </div>

              {modo === 'metros' && (
                <>
                  <div className="cf-row" style={{ marginTop: '0.5rem' }}>
                    <div className="cf-field" style={{ marginBottom: 0 }}>
                      <label>Poste especial (altura en cm)</label>
                      <input
                        type="number"
                        value={peAltura}
                        onChange={e => handlePeAlturaChange(e.target.value)}
                        placeholder="Ej: 98"
                        min="1" step="1"
                        disabled={!model}
                      />
                    </div>
                    <div className="cf-field" style={{ marginBottom: 0 }}>
                      <label>Referencia asignada</label>
                      <input
                        readOnly className="cf-auto"
                        value={peRef ? `${peRef.tipoKey} · ${peRef.tipoData.h}cm · ${peRef.precio?.toFixed(2)}€` : '—'}
                      />
                    </div>
                  </div>
                  {peSubst && (
                    <div className="pe-badge">
                      <span>
                        Poste especial <strong>{peSubst.altCm} cm</strong> →
                        ref. <strong>{peSubst.tipoKey} ({peSubst.h}cm)</strong> ·
                        sustituye poste <strong>{peSubst.substType}</strong> ·
                        <strong> {peSubst.precio.toFixed(2)} €/ud</strong>
                      </span>
                      <button className="pe-badge-clear" onClick={clearPosteEspecial} title="Quitar poste especial">✕</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Panel 4 — Accesorios */}
          <div className="calc-panel">
            <div className="calc-panel-header">
              <span className="cp-num">4</span>Accesorios y extras
            </div>
            <div className="calc-panel-body">
              <div className="cf-row">
                <div className="cf-field">
                  <label>Tapas por poste (4,20 €/ud)</label>
                  <input
                    type="number"
                    value={tapasQty}
                    onChange={e => setTapasQty(Math.max(0, parseInt(e.target.value) || 0))}
                    min="0"
                  />
                </div>
                <div className="cf-field">
                  <label>Coste de instalación (€)</label>
                  <input
                    type="number"
                    value={instalacion}
                    onChange={e => setInstalacion(e.target.value)}
                    placeholder="0.00"
                    min="0" step="any"
                  />
                </div>
              </div>
              {modo === 'metros' && (
                <div className="cf-row">
                  <div className="cf-field" style={{ marginBottom: 0 }}>
                    <label>Postes esquineros</label>
                    <input
                      type="number"
                      value={esquin}
                      onChange={e => setEsquin(Math.max(0, parseInt(e.target.value) || 0))}
                      min="0"
                    />
                  </div>
                  <div className="cf-field" style={{ marginBottom: 0 }}>
                    <label>Finales extra</label>
                    <input
                      type="number"
                      value={finalesExtra}
                      onChange={e => setFinalesExtra(Math.max(0, parseInt(e.target.value) || 0))}
                      min="0"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Panel 5 — Datos del cliente */}
          <div className="calc-panel">
            <div className="calc-panel-header">
              <span className="cp-num">5</span>Datos del cliente
            </div>
            <div className="calc-panel-body">
              <div className="cf-row">
                <div className="cf-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={cliente.nombre}
                    onChange={e => setCliente(c => ({ ...c, nombre: e.target.value }))}
                    placeholder="Nombre del cliente"
                  />
                </div>
                <div className="cf-field">
                  <label>Población</label>
                  <input
                    type="text"
                    value={cliente.poblacion}
                    onChange={e => setCliente(c => ({ ...c, poblacion: e.target.value }))}
                    placeholder="Ciudad / Municipio"
                  />
                </div>
              </div>
              <div className="cf-row">
                <div className="cf-field" style={{ marginBottom: 0 }}>
                  <label>Teléfono *</label>
                  <input
                    type="tel"
                    value={cliente.tel}
                    onChange={e => setCliente(c => ({ ...c, tel: e.target.value }))}
                    placeholder="600 000 000"
                  />
                </div>
                <div className="cf-field" style={{ marginBottom: 0 }}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={cliente.email}
                    onChange={e => setCliente(c => ({ ...c, email: e.target.value }))}
                    placeholder="cliente@email.com"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>{/* /calc-left */}

        {/* ─── COLUMNA DERECHA ─── */}
        <div className="calc-right">

          {/* Descuentos */}
          <div className="calc-panel disc-panel">
            <div className="calc-panel-header" style={{ background: '#1A1A1A', color: '#fff', borderBottom: '1px solid #333' }}>
              <span className="cp-num" style={{ background: '#E84300' }}>%</span>
              Descuentos y comisión
            </div>
            <div className="calc-panel-body" style={{ background: '#1A1A1A' }}>
              {/* Descuento manual del presupuesto (0–100%) */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '0.4rem' }}>
                  Descuento del presupuesto (%)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="0"
                    value={descuento}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === '') { setDescuento(0); return; }
                      const n = Math.min(100, Math.max(0, parseFloat(v) || 0));
                      setDescuento(n);
                    }}
                    style={{ width: '100%', padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', fontSize: '0.85rem' }}
                  />
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', fontWeight: 700 }}>%</span>
                </div>
              </div>
              {hasFinanciacion && (
                <>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>
                    Descuento por financiación
                  </div>
                  <div className="disc-btns">
                    {[0, 5, 10, 15, 20].map(d => (
                      <button
                        key={d}
                        className={`disc-btn${descuento === d ? ' disc-active' : ''}`}
                        onClick={() => setDescuento(d)}
                      >
                        {d === 0 ? 'Sin dto.' : `−${d}%`}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: descuento > 0 ? '#FFD700' : 'rgba(255,255,255,0.4)', marginBottom: '0.5rem', marginTop: '0.25rem' }}>
                    {descuento > 0
                      ? `Contado −${descuento}% · Financiado: +${finLive.recargoPct}% sobre el contado`
                      : `Sin descuento manual · Financiado: +${finLive.recargoPct}% sobre el contado`}
                  </div>
                  {/* Descuento manual */}
                  <div style={{ marginTop: '0.75rem', borderTop: '1px solid #333', paddingTop: '0.75rem' }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '0.4rem' }}>
                      Descuento manual (€)
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Importe €"
                      value={dtoManualAmt}
                      onChange={e => setDtoManualAmt(e.target.value)}
                      style={{ width: '100%', marginBottom: '0.4rem', padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', fontSize: '0.85rem' }}
                    />
                    <input
                      type="text"
                      placeholder="Concepto del descuento"
                      value={dtoManualConcepto}
                      onChange={e => setDtoManualConcepto(e.target.value)}
                      style={{ width: '100%', padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', fontSize: '0.85rem' }}
                    />
                  </div>
                </>
              )}

              {/* Coste adicional */}
              <div style={{ marginTop: '0.75rem', borderTop: '1px solid #333', paddingTop: '0.75rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '0.4rem' }}>
                  Coste adicional (€)
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Importe €"
                  value={extraCosteAmt}
                  onChange={e => setExtraCosteAmt(e.target.value)}
                  style={{ width: '100%', marginBottom: '0.4rem', padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', fontSize: '0.85rem' }}
                />
                <input
                  type="text"
                  placeholder="Concepto (ej: Transporte, Grúa...)"
                  value={extraCosteConcepto}
                  onChange={e => setExtraCosteConcepto(e.target.value)}
                  style={{ width: '100%', padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', fontSize: '0.85rem' }}
                />
              </div>

              {result && commPct > 0 && (
                <div className="comm-box">
                  <div className="comm-label">{isComercial ? 'Tu comisión' : 'Tu margen'}</div>
                  <div className="comm-amount">{fmt(commission)}</div>
                  <div className="comm-sub">
                    {commPct}% sobre base sin IVA{descuento > 0 ? ` (desc. −${descuento}%)` : ''}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Result box */}
          <div className="result-box">
            {result ? (
              <>
                <div className="rb-label">Total materiales (sin IVA)</div>
                <div className="rb-total">{fmt(baseTotal)}</div>
                {descuento > 0 && (
                  <div className="rb-discount">
                    <span className="disc-badge">−{descuento}%</span>
                    <span className="disc-price">{fmt(baseTotal * (1 - descuento / 100))}</span>
                  </div>
                )}
                {dtoManualNum > 0 && (
                  <div className="rb-discount">
                    <span className="disc-badge" style={{ background: '#1a6b3a' }}>−{fmt(dtoManualNum)}</span>
                    <span className="disc-price" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                      {dtoManualConcepto || 'Descuento manual'}
                    </span>
                  </div>
                )}
                <div className="rb-iva-row">
                  <span>Base imponible</span><span>{fmt(discTotal)}</span>
                </div>
                <div className="rb-iva-row">
                  <span>IVA 21%</span><span>{fmt(ivaAmt)}</span>
                </div>
                <div className="rb-total-iva">
                  <span>TOTAL CON IVA</span>
                  <span>{fmt(totalConIva)}</span>
                </div>
                <div className="rb-sub">
                  {result.modoTramos
                    ? `${model} · ${tramos.filter(t => t.metros > 0).length} tramos · ${result.metros.toFixed(2)} m · ${result.nPostes} postes`
                    : `${tipo} · ${tipos[tipo]?.h} cm · ${result.metros} m · ${result.lamasTotal} lamas · ${result.nPostes} postes`
                  }
                </div>
              </>
            ) : (
              <div className="rb-empty">
                Selecciona modelo, tipo y metros<br />para ver el presupuesto
              </div>
            )}
          </div>

          {/* Desglose */}
          {result && (
            <div className="breakdown-panel">
              <div className="bp-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Desglose de materiales</span>
                {editMode ? (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="bp-edit-btn bp-edit-btn--save" onClick={saveEditMaterials}>💾 Guardar cambios</button>
                    <button className="bp-edit-btn bp-edit-btn--reset" onClick={cancelEditMaterials}>✕ Cancelar</button>
                  </div>
                ) : savedItems ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span className="bp-edited-badge">Editado manualmente</span>
                    <button className="bp-edit-btn" onClick={startEditMaterials}>✏ Modificar</button>
                    <button className="bp-edit-btn bp-edit-btn--reset" onClick={resetEditItems}>↩ Restablecer</button>
                  </div>
                ) : (
                  <button className="bp-edit-btn" onClick={startEditMaterials}>✏ Modificar materiales</button>
                )}
              </div>
              {editMode ? (
                /* ── MODO EDICIÓN ACTIVA ── */
                <div className="bp-edit-table">
                  {editItems.length === 0 && (
                    <div className="bp-edit-empty">Sin materiales. Añade una línea.</div>
                  )}
                  {(() => {
                    const hasSections = editItems.some(it => it._section);
                    if (!hasSections) {
                      return editItems.map(it => <EditRow key={it._key} it={it} onUpdate={updateEditItem} onRemove={removeEditItem} fmt={fmt} />);
                    }
                    // Agrupar por _section preservando orden
                    const seen = [];
                    const groups = [];
                    editItems.forEach(it => {
                      if (!seen.includes(it._section)) { seen.push(it._section); groups.push({ label: it._section, items: [] }); }
                      groups[seen.indexOf(it._section)].items.push(it);
                    });
                    return groups.map(g => (
                      <div key={g.label} className="bp-edit-section">
                        <div className="bp-edit-section-header">{g.label}</div>
                        {g.items.map(it => <EditRow key={it._key} it={it} onUpdate={updateEditItem} onRemove={removeEditItem} fmt={fmt} />)}
                      </div>
                    ));
                  })()}
                  <button className="bp-edit-add" onClick={addEditItem}>＋ Añadir línea</button>
                  <div className="bp-edit-total">
                    <span>Total editado</span>
                    <span>{fmt(editItems.reduce((s, it) => s + (parseFloat(it.sub)||0), 0))}</span>
                  </div>
                </div>
              ) : (
                /* ── MODO NORMAL ── */
                <div className="bp-list">
                  {savedItems ? (
                    (() => {
                      const hasSections = savedItems.some(it => it._section);
                      const total = savedItems.reduce((s, it) => s + (parseFloat(it.sub)||0), 0);
                      if (!hasSections) return (
                        <>
                          {savedItems.map((it, i) => (
                            <div key={i} className="bp-item">
                              <div className="bi-left"><div className="bi-name">{it.name}</div>{it.detail && <div className="bi-detail">{it.detail}</div>}</div>
                              <div className="bi-price">{fmt(it.sub)}</div>
                            </div>
                          ))}
                          <div className="bp-grand-total"><span>TOTAL MATERIALES</span><span>{fmt(total)}</span></div>
                        </>
                      );
                      const seen = []; const groups = [];
                      savedItems.forEach(it => {
                        if (!seen.includes(it._section)) { seen.push(it._section); groups.push({ label: it._section, items: [] }); }
                        groups[seen.indexOf(it._section)].items.push(it);
                      });
                      return (
                        <>
                          {groups.map(g => (
                            <div key={g.label} className="bp-section">
                              <div className="bp-section-header">{g.label}</div>
                              {g.items.map((it, ii) => (
                                <div key={ii} className="bp-item">
                                  <div className="bi-left"><div className="bi-name">{it.name}</div>{it.detail && <div className="bi-detail">{it.detail}</div>}</div>
                                  <div className="bi-price">{fmt(it.sub)}</div>
                                </div>
                              ))}
                            </div>
                          ))}
                          <div className="bp-grand-total"><span>TOTAL MATERIALES</span><span>{fmt(total)}</span></div>
                        </>
                      );
                    })()
                  ) : result.sections ? (
                    <>
                      {result.sections.map((sec, si) => (
                        <div key={si} className="bp-section">
                          <div className="bp-section-header">{sec.label}</div>
                          {sec.items.map((it, ii) => (
                            <div key={ii} className="bp-item">
                              <div className="bi-left">
                                <div className="bi-name">{it.name}</div>
                                <div className="bi-detail">{it.detail}</div>
                              </div>
                              <div className="bi-price">{fmt(it.sub)}</div>
                            </div>
                          ))}
                          <div className="bp-section-total">
                            <span>Subtotal tramo</span><span>{fmt(sec.total)}</span>
                          </div>
                        </div>
                      ))}
                      {result.globalItems?.length > 0 && (
                        <div className="bp-section">
                          <div className="bp-section-header">Adicionales</div>
                          {result.globalItems.map((it, ii) => (
                            <div key={ii} className="bp-item">
                              <div className="bi-left">
                                <div className="bi-name">{it.name}</div>
                                <div className="bi-detail">{it.detail}</div>
                              </div>
                              <div className="bi-price">{fmt(it.sub)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="bp-grand-total">
                        <span>TOTAL MATERIALES</span>
                        <span>{fmt(result.total)}</span>
                      </div>
                    </>
                  ) : (
                    result.items.map((it, i) => (
                      <div key={i} className={`bp-item${it.ref === 'Poste especial' || it.ref === 'Esquinero' ? ' bp-item--special' : ''}`}>
                        <div className="bi-left">
                          <div className="bi-name">{it.name}</div>
                          <div className="bi-detail">{it.detail}</div>
                        </div>
                        <div className="bi-price">{fmt(it.sub)}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Botones de acción */}
          <div className="actions-panel">
            <button
              className="btn-pdf-financ"
              onClick={() => hasFinanciacion ? setPdfModal('download') : downloadPDF('contado')}
              disabled={!result}
            >
              ⬇ Descargar PDF
            </button>
            <div className="btn-separator" />
            <button
              className="btn-guardar-lead"
              onClick={guardarComoPresupuesto}
              disabled={!result || savingLead || !!savedLeadId}
            >
              {savingLead ? 'Guardando…' : savedLeadId ? '✅ Guardado' : '📋 Guardar presupuesto'}
            </button>
            {savedLeadId && (
              <div className="saved-lead-note">Ve a CRM Leads para verlo</div>
            )}
          </div>

          {/* Modal extras por tramo (poste especial o esquinero) */}
          {peModalTramo && (
            <div className="pdf-modal-overlay" onClick={() => setPeModalTramo(null)}>
              <div className="pdf-modal-card" onClick={e => e.stopPropagation()}>
                {peModalTramo.type === 'especial' && (!peModalTramo.step || peModalTramo.step === 1) ? (
                  <>
                    <div className="pdf-modal-title">Poste especial · {peModalTramo.altCm} cm</div>
                    <div className="pdf-modal-sub-desc">
                      Ref. asignada: <strong>{peModalTramo.ref.tipoKey} ({peModalTramo.ref.tipoData.h} cm)</strong> · {peModalTramo.ref.precio?.toFixed(2)} €/ud
                      <br/>Paso 1: ¿Qué tipo de poste especial es?
                    </div>
                    <div className="pdf-modal-btns">
                      <button className="pdf-modal-btn" onClick={() => selectTramoPostType('final')}>
                        <span className="pdf-modal-icon">🔚</span>
                        <span className="pdf-modal-label">Poste final</span>
                        <span className="pdf-modal-sub">Este poste especial es un final</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => selectTramoPostType('intermedio')}>
                        <span className="pdf-modal-icon">🔳</span>
                        <span className="pdf-modal-label">Poste intermedio</span>
                        <span className="pdf-modal-sub">Este poste especial es un intermedio</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => selectTramoPostType('esquinero')}>
                        <span className="pdf-modal-icon">📐</span>
                        <span className="pdf-modal-label">Poste esquinero</span>
                        <span className="pdf-modal-sub">Este poste especial es un esquinero</span>
                      </button>
                    </div>
                  </>
                ) : peModalTramo.type === 'especial' && peModalTramo.step === 2 ? (
                  <>
                    <div className="pdf-modal-title">Poste especial · {peModalTramo.altCm} cm · {peModalTramo.postType}</div>
                    <div className="pdf-modal-sub-desc">
                      Paso 2: ¿Qué poste elimina del tramo para sustituirlo?
                    </div>
                    <div className="pdf-modal-btns">
                      <button className="pdf-modal-btn" onClick={() => confirmTramoExtra('final')}>
                        <span className="pdf-modal-icon">🔚</span>
                        <span className="pdf-modal-label">Poste final</span>
                        <span className="pdf-modal-sub">Elimina un poste final del tramo</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => confirmTramoExtra('intermedio')}>
                        <span className="pdf-modal-icon">🔳</span>
                        <span className="pdf-modal-label">Poste intermedio</span>
                        <span className="pdf-modal-sub">Elimina un poste intermedio del tramo</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => confirmTramoExtra('esquinero')}>
                        <span className="pdf-modal-icon">📐</span>
                        <span className="pdf-modal-label">Poste esquinero</span>
                        <span className="pdf-modal-sub">Elimina un poste esquinero del tramo</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pdf-modal-title">Poste esquinero</div>
                    <div className="pdf-modal-sub-desc">
                      <strong>{peModalTramo.esquineroPrice?.toFixed(2)} €/ud</strong>
                      <br/>¿Qué poste de este tramo sustituye?
                    </div>
                    <div className="pdf-modal-btns">
                      <button className="pdf-modal-btn" onClick={() => confirmTramoExtra('final')}>
                        <span className="pdf-modal-icon">🔚</span>
                        <span className="pdf-modal-label">Poste final</span>
                        <span className="pdf-modal-sub">Elimina un poste final del tramo</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => confirmTramoExtra('intermedio')}>
                        <span className="pdf-modal-icon">🔳</span>
                        <span className="pdf-modal-label">Poste intermedio</span>
                        <span className="pdf-modal-sub">Elimina un poste intermedio del tramo</span>
                      </button>
                    </div>
                  </>
                )}
                <button className="pdf-modal-close" onClick={() => setPeModalTramo(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Modal poste especial */}
          {peModal && peRef && (
            <div className="pdf-modal-overlay" onClick={() => { setPeModal(false); setPeAltura(''); setPeRef(null); setPeModalStep(1); setPePostType(null); }}>
              <div className="pdf-modal-card" onClick={e => e.stopPropagation()}>
                <div className="pdf-modal-title">Poste especial · {peAltura} cm</div>
                {peModalStep === 1 ? (
                  <>
                    <div className="pdf-modal-sub-desc">
                      Ref. asignada: <strong>{peRef.tipoKey} ({peRef.tipoData.h} cm)</strong> · {peRef.precio?.toFixed(2)} €/ud
                      <br/>Paso 1: ¿Qué tipo de poste especial es?
                    </div>
                    <div className="pdf-modal-btns">
                      <button className="pdf-modal-btn" onClick={() => selectPePostType('final')}>
                        <span className="pdf-modal-icon">🔚</span>
                        <span className="pdf-modal-label">Poste final</span>
                        <span className="pdf-modal-sub">Este poste especial es un final</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => selectPePostType('intermedio')}>
                        <span className="pdf-modal-icon">🔳</span>
                        <span className="pdf-modal-label">Poste intermedio</span>
                        <span className="pdf-modal-sub">Este poste especial es un intermedio</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => selectPePostType('esquinero')}>
                        <span className="pdf-modal-icon">📐</span>
                        <span className="pdf-modal-label">Poste esquinero</span>
                        <span className="pdf-modal-sub">Este poste especial es un esquinero</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pdf-modal-sub-desc">
                      Tipo: <strong>{pePostType}</strong> · Ref. <strong>{peRef.tipoKey} ({peRef.tipoData.h} cm)</strong> · {peRef.precio?.toFixed(2)} €/ud
                      <br/>Paso 2: ¿Qué poste elimina del cálculo para sustituirlo?
                    </div>
                    <div className="pdf-modal-btns">
                      <button className="pdf-modal-btn" onClick={() => confirmPosteEspecial('final')}>
                        <span className="pdf-modal-icon">🔚</span>
                        <span className="pdf-modal-label">Poste final</span>
                        <span className="pdf-modal-sub">Elimina un poste final del cálculo</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => confirmPosteEspecial('intermedio')}>
                        <span className="pdf-modal-icon">🔳</span>
                        <span className="pdf-modal-label">Poste intermedio</span>
                        <span className="pdf-modal-sub">Elimina un poste intermedio del cálculo</span>
                      </button>
                      <button className="pdf-modal-btn" onClick={() => confirmPosteEspecial('esquinero')}>
                        <span className="pdf-modal-icon">📐</span>
                        <span className="pdf-modal-label">Poste esquinero</span>
                        <span className="pdf-modal-sub">Elimina un poste esquinero del cálculo</span>
                      </button>
                    </div>
                  </>
                )}
                <button className="pdf-modal-close" onClick={() => { setPeModal(false); setPeAltura(''); setPeRef(null); setPeModalStep(1); setPePostType(null); }}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Modal selección PDF (compartido: save | download) */}
          {pdfModal !== null && (
            <div className="pdf-modal-overlay" onClick={() => setPdfModal(null)}>
              <div className="pdf-modal-card" onClick={e => e.stopPropagation()}>
                <div className="pdf-modal-title">
                  {pdfModal === 'save' ? '💾 Guardar PDF en lead' : '⬇ Descargar PDF'}
                </div>
                <div className="pdf-modal-sub-desc">
                  {pdfModal === 'save'
                    ? 'Se guardará como nota en el lead del cliente (requiere teléfono)'
                    : 'Se abrirá en una nueva pestaña para imprimir o guardar'}
                </div>
                <div className="pdf-modal-btns">
                  <button className="pdf-modal-btn" onClick={() => {
                    pdfModal === 'save' ? savePDFToLead('contado') : downloadPDF('contado');
                    setPdfModal(null);
                  }}>
                    <span className="pdf-modal-icon">📄</span>
                    <span className="pdf-modal-label">Contado</span>
                    <span className="pdf-modal-sub">Precio al contado sin financiar</span>
                  </button>
                  <button className="pdf-modal-btn" onClick={() => {
                    pdfModal === 'save' ? savePDFToLead('financiado') : downloadPDF('financiado');
                    setPdfModal(null);
                  }}>
                    <span className="pdf-modal-icon">🏦</span>
                    <span className="pdf-modal-label">Financiado</span>
                    <span className="pdf-modal-sub">Proforma BBVA en 36 cuotas</span>
                  </button>
                  <button className="pdf-modal-btn pdf-modal-btn--both" onClick={() => {
                    pdfModal === 'save' ? savePDFToLead('ambos') : downloadPDF('ambos');
                    setPdfModal(null);
                  }}>
                    <span className="pdf-modal-icon">📋</span>
                    <span className="pdf-modal-label">Ambos</span>
                    <span className="pdf-modal-sub">Contado y financiado a la vez</span>
                  </button>
                </div>
                <button className="pdf-modal-close" onClick={() => setPdfModal(null)}>Cancelar</button>
              </div>
            </div>
          )}

        </div>{/* /calc-right */}
      </div>
    </div>
  );
}
